import React, { useState, useEffect } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuLinks, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
function HeaderContent() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header data-section-id="21753" className={`sticky top-0 z-50 w-full transition-all duration-300 ease-in-out ${isScrolled ? "bg-[#020617]/70 backdrop-blur-[15px] py-3 border-b border-white/10 shadow-lg" : "bg-[#020617] py-5 border-b border-white/5"}`}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
          {}
          <Link to="/HomePage" className="flex items-center gap-2 z-50">
            <img src="https://musical-parrot.10web.cloud/wp-content/uploads/2026/03/NEXEL-Logo.png" alt="Nexel Technologies Modern Logo" className="h-7 w-auto object-contain drop-shadow-[0_0_8px_rgba(0,255,255,0.6)]" />
          </Link>

          {}
          <div className="hidden md:flex items-center gap-8">
            {!loading && <NavigationMenu viewport={false}>
                <NavigationMenuList className="gap-1">
                  {menuItems.map(item => <NavigationMenuItem key={item.id}>
                      <NavigationMenuLink asChild>
                        <Link to={item.href || "#"} className="group relative px-3 py-2 text-sm font-mono font-medium text-cyan-400/90 hover:text-cyan-300 transition-colors tracking-wider">
                          {item.label}
                          <span className="absolute -bottom-1 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-cyan-400 opacity-0 transition-all duration-300 group-hover:opacity-100 shadow-[0_0_8px_#00ffff]" />
                        </Link>
                      </NavigationMenuLink>
                    </NavigationMenuItem>)}
                </NavigationMenuList>
              </NavigationMenu>}
            
            <div className="flex items-center gap-4">
              <Button variant="default" size="sm" className="font-mono text-xs tracking-widest bg-cyan-500 text-[#020617] hover:bg-cyan-400 shadow-[0_0_20px_rgba(0,255,255,0.4)] rounded-md transition-all duration-300">
                GLOBAL ACCESS
              </Button>
              <Button variant="outline" size="sm" asChild className="font-mono text-xs tracking-widest border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500 transition-all duration-300">
                <Link to="https://wa.me/8615817452996" newTab>
                  <MessageCircle className="mr-1" />
                  WHATSAPP
                </Link>
              </Button>
            </div>
          </div>

          {}
          <Button variant="ghost" size="icon" className="md:hidden z-50 text-slate-200 hover:text-cyan-400 hover:bg-white/5" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>

          {}
          <div className={`fixed inset-0 z-40 bg-[#020617]/95 backdrop-blur-2xl transition-transform duration-300 md:hidden flex flex-col items-center justify-center gap-8 ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
            <nav className="flex flex-col items-center gap-6">
              {!loading && menuItems.map(item => <Link key={item.id} to={item.href || "#"} className="text-2xl font-mono font-medium text-slate-200 hover:text-cyan-400 transition-colors relative group tracking-widest" onClick={() => setIsMobileMenuOpen(false)}>
                    {item.label}
                    <span className="absolute -bottom-2 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-cyan-400 opacity-0 transition-all duration-300 group-hover:opacity-100 shadow-[0_0_10px_#00ffff]" />
                  </Link>)}
            </nav>
            <div className="flex flex-col items-center gap-4 w-full px-8">
              <Button variant="default" size="lg" className="font-mono bg-cyan-500 text-[#020617] hover:bg-cyan-400 shadow-[0_0_25px_rgba(0,255,255,0.5)] rounded-md w-full max-w-[280px]">
                GLOBAL ACCESS
              </Button>
              <Button variant="outline" size="lg" asChild className="font-mono border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500 w-full max-w-[280px]">
                <Link to="https://wa.me/8615817452996" newTab>
                  <MessageCircle className="mr-2 h-5 w-5" />
                  WHATSAPP
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="fixed bottom-4 right-4 z-50">
        <Button variant="outline" size="sm" asChild className="font-mono text-xs tracking-widest border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500 transition-all duration-300">
          <Link to="https://wa.me/8615817452996" newTab>
            <MessageCircle className="mr-1" />
            WHATSAPP
          </Link>
        </Button>
      </div>
    </>;
}
export default function NavigationPortal() {
  return <section id="header" className="dark bg-[#020617] text-slate-200 w-full">
      <MenuProvider menu_id="44" className="w-full">
        <HeaderContent />
      </MenuProvider>
    </section>;
}